import { useEffect, useState } from 'react';
import api from '../lib/api';

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.blocks)) return value.blocks;
  if (Array.isArray(value?.transactions)) return value.transactions;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const formatValue = (value, fallback = '—') => {
  if (value === undefined || value === null || value === '') return fallback;
  return `${value}`;
};

export default function Explorer() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chainInfo, setChainInfo] = useState(null);
  const [latestBlock, setLatestBlock] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [syncState, setSyncState] = useState(null);
  const [connectivity, setConnectivity] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const results = await Promise.allSettled([
          api.chain.info(),
          api.chain.latestBlock(),
          api.chain.blocks(10, 0),
          api.chain.transactions(10, 1),
          api.sync.state(),
          api.chain.connectivity(),
        ]);

        if (results[0].status === 'fulfilled') setChainInfo(results[0].value);
        if (results[1].status === 'fulfilled') setLatestBlock(results[1].value);
        if (results[2].status === 'fulfilled') setBlocks(asArray(results[2].value));
        if (results[3].status === 'fulfilled') setTransactions(asArray(results[3].value));
        if (results[4].status === 'fulfilled') setSyncState(results[4].value);
        if (results[5].status === 'fulfilled') setConnectivity(results[5].value);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Unable to load explorer data.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  return (
    <div className="space-y-6">
      <section className="glass p-6">
        <p className="text-sm uppercase tracking-wide text-slate-400">Explorer</p>
        <h1 className="mt-2 text-3xl font-semibold text-white">Chain visibility</h1>
        <p className="mt-2 text-slate-300">
          Inspect chain metadata, block progression, transaction flow and sync status from the live API.
        </p>
      </section>

      {error ? (
        <section className="glass border border-rose-500/30 p-4 text-rose-200">{error}</section>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <article className="glass p-5">
          <p className="text-sm text-slate-400">Latest block</p>
          <h2 className="mt-2 text-2xl font-semibold text-white">
            {loading
              ? 'Loading…'
              : formatValue(latestBlock?.height || latestBlock?.number || latestBlock?.block_height)}
          </h2>
        </article>

        <article className="glass p-5">
          <p className="text-sm text-slate-400">Network name</p>
          <h2 className="mt-2 text-2xl font-semibold text-white">
            {formatValue(chainInfo?.network || chainInfo?.chain || chainInfo?.name, 'ITANI')}
          </h2>
        </article>

        <article className="glass p-5">
          <p className="text-sm text-slate-400">Sync state</p>
          <h2 className="mt-2 text-2xl font-semibold text-white">
            {formatValue(syncState?.status || syncState?.state || syncState?.sync_status, 'Unknown')}
          </h2>
        </article>

        <article className="glass p-5">
          <p className="text-sm text-slate-400">Connectivity</p>
          <h2 className="mt-2 text-2xl font-semibold text-white">
            {formatValue(
              connectivity?.status || connectivity?.state || (connectivity?.connected ? 'Connected' : ''),
              'Unknown'
            )}
          </h2>
        </article>
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <article className="glass p-6">
          <h2 className="text-xl font-semibold text-white">Chain info</h2>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
              <span className="text-slate-400">Chain ID</span>
              <span className="text-white">{formatValue(chainInfo?.chain_id || chainInfo?.id)}</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
              <span className="text-slate-400">Peers</span>
              <span className="text-white">{formatValue(connectivity?.peers || chainInfo?.peers, '—')}</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
              <span className="text-slate-400">Latest hash</span>
              <span className="max-w-[220px] truncate text-right text-white">
                {formatValue(latestBlock?.hash || latestBlock?.block_hash, '—')}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-slate-400">Sync detail</span>
              <span className="max-w-[220px] text-right text-white">
                {formatValue(syncState?.detail || syncState?.message, 'No extra detail')}
              </span>
            </div>
          </div>
        </article>

        <article className="glass p-6">
          <h2 className="text-xl font-semibold text-white">Recent blocks</h2>
          {blocks.length ? (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-slate-400">
                  <tr>
                    <th className="pb-3 pr-4 font-medium">Height</th>
                    <th className="pb-3 pr-4 font-medium">Hash</th>
                    <th className="pb-3 font-medium">Tx count</th>
                  </tr>
                </thead>
                <tbody>
                  {blocks.map((block, index) => (
                    <tr key={block?.hash || block?.height || index} className="border-t border-white/5">
                      <td className="py-3 pr-4 text-white">
                        {formatValue(block?.height || block?.number || block?.block_height)}
                      </td>
                      <td className="max-w-[220px] truncate py-3 pr-4 text-slate-300">
                        {formatValue(block?.hash || block?.block_hash)}
                      </td>
                      <td className="py-3 text-slate-300">
                        {formatValue(block?.tx_count || block?.transactions?.length || block?.count, '0')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4 text-slate-300">
              {loading ? 'Loading blocks…' : 'No block list was returned by the API.'}
            </p>
          )}
        </article>
      </section>

      <section className="glass p-6">
        <h2 className="text-xl font-semibold text-white">Recent transactions</h2>
        {transactions.length ? (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-slate-400">
                <tr>
                  <th className="pb-3 pr-4 font-medium">Hash / ID</th>
                  <th className="pb-3 pr-4 font-medium">Type</th>
                  <th className="pb-3 pr-4 font-medium">Amount</th>
                  <th className="pb-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => (
                  <tr key={transaction?.hash || transaction?.id || index} className="border-t border-white/5">
                    <td className="max-w-[220px] truncate py-3 pr-4 text-white">
                      {formatValue(
                        transaction?.hash ||
                          transaction?.txid ||
                          transaction?.id ||
                          transaction?.transaction_id,
                        'Pending'
                      )}
                    </td>
                    <td className="py-3 pr-4 text-slate-300">
                      {formatValue(transaction?.type || transaction?.kind || transaction?.direction)}
                    </td>
                    <td className="py-3 pr-4 text-slate-300">
                      {formatValue(
                        transaction?.amount ||
                          transaction?.value ||
                          transaction?.amount_in ||
                          transaction?.amount_out,
                        '—'
                      )}
                    </td>
                    <td className="py-3 text-slate-300">
                      {formatValue(transaction?.status || transaction?.state, 'Confirmed')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-4 text-slate-300">
            {loading ? 'Loading transactions…' : 'No recent transactions were returned by the API.'}
          </p>
        )}
      </section>
    </div>
  );
}